// =============================================================================
// AudioPlayer.tsx - Compact blog post text-to-speech player + sticky mini-player
// Voice: "Tessa" (South African English, widely available) with fallback chain
// Speeds: 0.75× 1.15× 1.5× only (default 1.15×)
// Design: single compact row, dark editorial, teal (#00D4AA) accent on #0F1419
//
// Chromium fixes:
//   1. requestAnimationFrame defer after cancel() to avoid same-tick race
//   2. Voice-ready guard: play button disabled until voices are loaded
//   3. Chunk validation: skip empty chunks
//   4. Voice resolved at speak-time from ref (avoids stale closure)
// =============================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Play, Pause, Square, Headphones, X, ArrowUp } from "lucide-react";
import { trackAudio } from "@/lib/analytics";

interface AudioPlayerProps {
  text: string;
  title: string;
  readingTime: number;
  /** Blog post slug for GA4 event dimensions (e.g. "write-content-cited-chatgpt-answers") */
  postSlug?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function stripMarkdown(md: string): string {
  return md
    .replace(/#{1,6}\s+/g, " ")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`{1,3}[^`]*`{1,3}/g, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/^>\s+/gm, "")
    .replace(/---+/g, " ")
    .replace(/\|[^\n]+\|/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function splitIntoChunks(text: string, maxLen = 180): string[] {
  const sentences = text.match(/[^.!?]+[.!?]+\s*/g) || [text];
  const chunks: string[] = [];
  let current = "";
  for (const s of sentences) {
    if ((current + s).length > maxLen && current.length > 0) {
      const trimmed = current.trim();
      if (trimmed) chunks.push(trimmed);
      current = s;
    } else {
      current += s;
    }
  }
  const last = current.trim();
  if (last) chunks.push(last);
  return chunks.filter((c) => c.length > 0);
}

function safeCancel() {
  try {
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }
  } catch (_) { /* ignore */ }
}

// Pick "Tessa" or best available English voice
function pickTessa(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  return (
    voices.find((v) => v.name === "Tessa") ||
    voices.find((v) => v.name.toLowerCase().includes("tessa")) ||
    voices.find((v) => v.name === "Google UK English Female") ||
    voices.find((v) => v.name.includes("Google UK English")) ||
    voices.find((v) => v.name.includes("Google US English")) ||
    voices.find((v) => v.lang === "en-GB" && !v.name.includes("eSpeak")) ||
    voices.find((v) => v.lang.startsWith("en") && !v.name.includes("eSpeak")) ||
    voices[0] ||
    null
  );
}

const SPEEDS = [0.75, 1.15, 1.5];

// ── Component ─────────────────────────────────────────────────────────────────

export default function AudioPlayer({ text, title, readingTime, postSlug = "" }: AudioPlayerProps) {
  const slug = postSlug || (typeof window !== "undefined" ? window.location.pathname.replace(/^\/blog\//, "").replace(/\/$/, "") : "");
  const [mounted, setMounted] = useState(false);
  const [supported, setSupported] = useState(true);
  const [voicesReady, setVoicesReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [rate, setRate] = useState(1.15);
  const [miniVisible, setMiniVisible] = useState(false);
  const [miniDismissed, setMiniDismissed] = useState(false);

  const chunksRef = useRef<string[]>([]);
  const chunkIndexRef = useRef(0);
  const rateRef = useRef(rate);
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null);
  const isPlayingRef = useRef(false);
  const mainPlayerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => { rateRef.current = rate; }, [rate]);

  // ── Load voices ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!("speechSynthesis" in window)) {
      setSupported(false);
      return;
    }

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        const picked = pickTessa(voices);
        voiceRef.current = picked;
        setVoicesReady(true);
      }
    };

    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    const t1 = setTimeout(loadVoices, 100);
    const t2 = setTimeout(loadVoices, 500);

    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // ── Prepare chunks ────────────────────────────────────────────────────────
  useEffect(() => {
    const clean = stripMarkdown(text);
    const chunks = splitIntoChunks(clean, 180);
    chunksRef.current = chunks;
    setTotalChunks(chunks.length);
    setCurrentChunk(0);
    chunkIndexRef.current = 0;
  }, [text]);

  // ── IntersectionObserver for mini-player ─────────────────────────────────
  useEffect(() => {
    if (!mainPlayerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && isPlayingRef.current) {
          setMiniVisible(true);
          trackAudio({ action: "mini_player_shown", post_slug: slug, post_title: title, speed: rateRef.current });
        } else {
          setMiniVisible(false);
          setMiniDismissed(false);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(mainPlayerRef.current);
    return () => observer.disconnect();
  }, [slug, title]);

  useEffect(() => {
    isPlayingRef.current = playing || paused;
    if (!playing && !paused) {
      setMiniVisible(false);
      setMiniDismissed(false);
    }
  }, [playing, paused]);

  // ── Core speak ────────────────────────────────────────────────────────────
  const speakChunk = useCallback((index: number) => {
    if (!isPlayingRef.current) return;
    if (index >= chunksRef.current.length) {
      setPlaying(false);
      setPaused(false);
      setCurrentChunk(chunksRef.current.length);
      chunkIndexRef.current = 0;
      isPlayingRef.current = false;
      setMiniVisible(false);
      trackAudio({ action: "complete", post_slug: slug, post_title: title, speed: rateRef.current, progress_pct: 100 });
      return;
    }

    const chunkText = chunksRef.current[index];
    if (!chunkText || chunkText.trim().length === 0) {
      speakChunk(index + 1);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(chunkText);
    utterance.rate = rateRef.current;
    utterance.volume = 1;
    utterance.pitch = 1;

    const currentVoice =
      voiceRef.current ??
      pickTessa(window.speechSynthesis.getVoices()) ??
      null;
    if (currentVoice) utterance.voice = currentVoice;

    utterance.onstart = () => {
      chunkIndexRef.current = index;
      setCurrentChunk(index);
    };

    utterance.onend = () => {
      if (isPlayingRef.current) speakChunk(index + 1);
    };

    utterance.onerror = (e) => {
      if (e.error === "interrupted" || e.error === "canceled") return;
      if (isPlayingRef.current) setTimeout(() => speakChunk(index + 1), 50);
    };

    requestAnimationFrame(() => {
      if (isPlayingRef.current) window.speechSynthesis.speak(utterance);
    });
  }, []);

  // ── Controls ──────────────────────────────────────────────────────────────
  const handlePlay = useCallback(() => {
    if (!supported || !voicesReady) return;
    if (paused) {
      window.speechSynthesis.resume();
      setPlaying(true);
      setPaused(false);
      isPlayingRef.current = true;
      trackAudio({ action: "play", post_slug: slug, post_title: title, speed: rateRef.current, progress_pct: totalChunks > 0 ? Math.round((chunkIndexRef.current / totalChunks) * 100) : 0 });
      return;
    }
    safeCancel();
    isPlayingRef.current = true;
    setPlaying(true);
    setPaused(false);
    trackAudio({ action: "play", post_slug: slug, post_title: title, speed: rateRef.current, progress_pct: 0 });
    requestAnimationFrame(() => speakChunk(chunkIndexRef.current));
  }, [supported, voicesReady, paused, speakChunk, slug, title, totalChunks]);

  const handlePause = useCallback(() => {
    window.speechSynthesis.pause();
    setPlaying(false);
    setPaused(true);
    isPlayingRef.current = false;
    trackAudio({ action: "pause", post_slug: slug, post_title: title, speed: rateRef.current, progress_pct: totalChunks > 0 ? Math.round((chunkIndexRef.current / totalChunks) * 100) : 0 });
  }, [slug, title, totalChunks]);

  const handleStop = useCallback(() => {
    const pct = totalChunks > 0 ? Math.round((chunkIndexRef.current / totalChunks) * 100) : 0;
    isPlayingRef.current = false;
    safeCancel();
    setPlaying(false);
    setPaused(false);
    setCurrentChunk(0);
    setMiniVisible(false);
    chunkIndexRef.current = 0;
    trackAudio({ action: "stop", post_slug: slug, post_title: title, speed: rateRef.current, progress_pct: pct });
  }, [slug, title, totalChunks]);

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      isPlayingRef.current = false;
      safeCancel();
    };
  }, []);

  // Do not render during SSR / before hydration - prevents
  // "Loading… 0.75× 1× 1.25×" artefacts visible to crawlers.
  if (!mounted) return null;

  // When speechSynthesis is unavailable, show a visible but disabled player
  // so the bar is always present on the page (never silently absent).
  if (!supported) {
    return (
      <div className="rounded-lg border border-[#00D4AA]/20 bg-[#0F1419] overflow-hidden">
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="w-7 h-7 rounded-md bg-[#00D4AA]/12 border border-[#00D4AA]/20 flex items-center justify-center shrink-0">
            <Headphones className="w-3.5 h-3.5 text-[#00D4AA]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/50 text-xs font-mono leading-none">Audio not available in this browser</p>
          </div>
        </div>
      </div>
    );
  }

  const estimatedListenMin = Math.max(1, Math.ceil(readingTime / rate));
  const progressPct = Math.min(100, totalChunks > 0 ? Math.round((currentChunk / totalChunks) * 100) : 0);
  const showMini = miniVisible && !miniDismissed && (playing || paused);
  const canPlay = voicesReady && chunksRef.current.length > 0;

  const PlayPauseBtn = ({ sm = false }: { sm?: boolean }) => (
    <button
      onClick={playing ? handlePause : handlePlay}
      disabled={!canPlay}
      className={`rounded-full bg-[#00D4AA] text-[#0F1419] flex items-center justify-center hover:bg-[#00BFA0] active:scale-95 transition-all duration-150 shrink-0 shadow-[0_0_10px_rgba(0,212,170,0.25)] disabled:opacity-40 disabled:cursor-not-allowed ${sm ? "w-7 h-7" : "w-8 h-8"}`}
      aria-label={playing ? "Pause" : paused ? "Resume" : "Play"}
    >
      {playing
        ? <Pause className={sm ? "w-3 h-3" : "w-3.5 h-3.5"} />
        : <Play className={`${sm ? "w-3 h-3" : "w-3.5 h-3.5"} ml-0.5`} />}
    </button>
  );

  return (
    <>
      {/* ── Main player - compact single row ── */}
      <div
        ref={mainPlayerRef}
        className="rounded-lg border border-[#00D4AA]/20 bg-[#0F1419]/70 backdrop-blur-sm overflow-hidden"
      >
        {/* Thin progress bar */}
        <div className="h-0.5 bg-white/5">
          <div className="h-full bg-[#00D4AA] transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>

        <div className="flex items-center gap-3 px-3 py-2.5">
          {/* Icon */}
          <div className="w-7 h-7 rounded-md bg-[#00D4AA]/12 border border-[#00D4AA]/20 flex items-center justify-center shrink-0">
            <Headphones className="w-3.5 h-3.5 text-[#00D4AA]" />
          </div>

          {/* Label */}
          <div className="flex-1 min-w-0">
            <p className="text-white/80 text-xs font-mono leading-none">
              {!voicesReady
                ? "Loading…"
                : playing || paused
                  ? `${progressPct}% · ~${estimatedListenMin} min`
                  : `Listen · ~${estimatedListenMin} min`}
            </p>
          </div>

          {/* Speed buttons */}
          <div className="flex items-center gap-0.5">
            {SPEEDS.map((r) => (
              <button
                key={r}
                onClick={() => { setRate(r); trackAudio({ action: "speed_change", post_slug: slug, post_title: title, speed: r, progress_pct: totalChunks > 0 ? Math.round((currentChunk / totalChunks) * 100) : 0 }); }}
                className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition-colors ${
                  rate === r
                    ? "bg-[#00D4AA]/20 text-[#00D4AA] border border-[#00D4AA]/30"
                    : "text-white/30 hover:text-white/60"
                }`}
              >
                {r}×
              </button>
            ))}
          </div>

          {/* Play/Pause */}
          <PlayPauseBtn />

          {/* Stop */}
          <button
            onClick={handleStop}
            disabled={!playing && !paused}
            className="w-6 h-6 rounded-full border border-white/12 text-white/35 hover:text-white/65 hover:border-white/25 flex items-center justify-center transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
            aria-label="Stop"
          >
            <Square className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>

      {/* ── Sticky mini-player ── */}
      {showMini && createPortal(
        <div
          role="region"
          aria-label="Audio mini-player"
          style={{ animation: "miniPlayerSlideUp 220ms cubic-bezier(0.23,1,0.32,1) both" }}
          className="fixed bottom-0 left-0 right-0 z-[9999] px-3 pb-3 pointer-events-none"
        >
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <div className="rounded-xl border border-[#00D4AA]/30 bg-[#0F1419]/95 backdrop-blur-xl shadow-[0_-4px_32px_rgba(0,0,0,0.5)] overflow-hidden">
              <div className="h-0.5 bg-white/5">
                <div className="h-full bg-[#00D4AA] transition-all duration-300" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="flex items-center gap-2.5 px-4 py-2.5">
                <div className="w-6 h-6 rounded-md bg-[#00D4AA]/12 border border-[#00D4AA]/20 flex items-center justify-center shrink-0">
                  <Headphones className="w-3 h-3 text-[#00D4AA]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-semibold truncate leading-tight">{title}</p>
                  <p className="text-white/35 text-[10px] font-mono">{progressPct}% · {rate}×</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* Speed cycle */}
                  <button
                    onClick={() => {
                      const idx = SPEEDS.indexOf(rate);
                      setRate(SPEEDS[(idx + 1) % SPEEDS.length]);
                    }}
                    className="px-1.5 py-0.5 text-[10px] font-mono rounded border border-white/15 text-white/40 hover:text-white/70 hover:border-white/30 transition-colors"
                  >
                    {rate}×
                  </button>
                  <PlayPauseBtn sm />
                  <button
                    onClick={handleStop}
                    className="w-6 h-6 rounded-full border border-white/12 text-white/35 hover:text-white/65 hover:border-white/25 flex items-center justify-center transition-colors"
                    aria-label="Stop"
                  >
                    <Square className="w-2.5 h-2.5" />
                  </button>
                  <button
                    onClick={() => mainPlayerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
                    className="w-6 h-6 rounded-full border border-white/12 text-white/35 hover:text-[#00D4AA] hover:border-[#00D4AA]/40 flex items-center justify-center transition-colors"
                    aria-label="Back to main player"
                  >
                    <ArrowUp className="w-2.5 h-2.5" />
                  </button>
                  <button
                    onClick={() => { setMiniDismissed(true); trackAudio({ action: "mini_player_dismissed", post_slug: slug, post_title: title, speed: rateRef.current, progress_pct: totalChunks > 0 ? Math.round((currentChunk / totalChunks) * 100) : 0 }); }}
                    className="w-6 h-6 rounded-full text-white/25 hover:text-white/60 flex items-center justify-center transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
